import { useAppSelector } from "@store/store-helper";
import {
  fetchingProjectsFlagsSelector,
  projectMembersSelector,
} from "@store/projects/projects-selector";
import { MEMBERS_TABLE_COLUMNS } from "@components/table/members/members-table-constants";
import { MembersTable } from "@components/table/members/members-table";
import { MembersSort } from "@components/table/members/members-sort";
import { DEFAULT_MEMBERS_SORT_BY } from "@components/table/members/members-sort-constants";
import { TeamTabProjectDetails } from "@pages/project-details/project-team/team-tab-project-details";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { MembersSortBy } from "@custom-types/member-types";
import { isMembersSortBy } from "@custom-types/type-guards";
import { useProjectTeamBulkActions } from "@pages/project-details/project-team/use-project-team-bulk-actions";
import { SdbProject } from "@custom-types/project-types";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { useMemo } from "react";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { getExportComponent, sortMembers } from "@utils/member-utils";
import { useMediaQueryList } from "@hooks/use-media-query";
import { ProjectTeamMobileView } from "@pages/project-details/project-team/project-team-mobile-view";

interface Props extends Partial<BaseCompanyIdProps> {
  /** The project to display */
  project?: SdbProject;

  /** Whether the project is still loading and skeleton should be shown instead */
  isLoading: boolean;
}

/** Members columns without the "user" column */
const projectTeamMembersColumns = [...MEMBERS_TABLE_COLUMNS].filter(
  (col) => col !== "user" && col !== "joinedDate"
);

/**
 * Contains the Team tab of the project details
 */
export function ProjectTeam({
  project,
  companyId,
  isLoading,
}: Props): JSX.Element {
  const projectMembers = useAppSelector(projectMembersSelector);
  const { isFetchingProjects } = useAppSelector(fetchingProjectsFlagsSelector);
  const { isScreenSmOrSmaller } = useMediaQueryList();

  const { sortBy, setSortBy } = useSubjectsSortByQuery<MembersSortBy>({
    defaultSort: DEFAULT_MEMBERS_SORT_BY,
    isValidSort: isMembersSortBy,
  });

  const sortedProjectMembers = sortMembers(projectMembers, sortBy);
  const buttonComponents = useProjectTeamBulkActions({
    companyId,
    project,
    members: sortedProjectMembers,
  });

  const shouldHideCheckbox = useMemo(
    () => project?.archivingState === "ARCHIVED",
    [project?.archivingState]
  );

  return (
    <>
      <TeamTabProjectDetails project={project} isLoading={isLoading} />

      <EntitiesToolbar
        toolbarEntityName="Member"
        numberOfRows={sortedProjectMembers.length}
        isLoading={isFetchingProjects || isLoading}
        sortActions={
          sortedProjectMembers.length > 0 && (
            <MembersSort
              sortBy={sortBy}
              setSortBy={setSortBy}
              tableSubject="project"
            />
          )
        }
        exportComponent={getExportComponent(
          sortedProjectMembers.length,
          buttonComponents.export
        )}
      />

      {isScreenSmOrSmaller && (
        <ProjectTeamMobileView
          members={sortedProjectMembers}
          isLoading={isLoading}
          bulkActionButtons={buttonComponents}
          selectedProject={project}
          sortBy={sortBy}
        />
      )}

      {!isScreenSmOrSmaller && (
        <ColumnPreferencesProvider
          tableKey="project-team-members-table"
          columns={projectTeamMembersColumns}
          options={{
            nonConfigurableColumns: ["options"],
            defaultVisibleColumns: ["name", "email", "status", "role"],
          }}
        >
          <MembersTable
            companyId={companyId}
            members={sortedProjectMembers}
            project={project}
            tableSubject={"project"}
            isLoading={isFetchingProjects || isLoading}
            bulkActionButtons={buttonComponents}
            shouldHideCheckbox={shouldHideCheckbox}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        </ColumnPreferencesProvider>
      )}
    </>
  );
}
